import { AwsRum } from 'aws-rum-web';
import { redactForRum } from './rum-redact';
import { scrubValuePatterns } from './rum-scrub';
import type { RumConfig } from './rum-config';

/**
 * CloudWatch RUM telemetry for the policy-tracker SPA (#3019).
 *
 * THE IRON RULE: RUM must NEVER break app bootstrap or change any user-facing
 * behaviour. Every interaction with the RUM SDK — config consumption, the
 * constructor, recordError, recordEvent, the fetch wrapper, and the global
 * handlers wired in `main.tsx` — is wrapped in try/catch and no-ops when the
 * client is uninitialised. `initRum` cannot reject or throw. Telemetry is
 * strictly best-effort.
 *
 * PII: every metadata object attached to an event is passed through
 * `redactForRum` (mirrors the backend `PII_LOG_RULES`) and request/response
 * bodies are stripped. We never send a request or response body to RUM.
 */

/** Event type used for application errors recorded via {@link recordError}. */
const ERROR_EVENT_TYPE = 'accedo_error';
/** Event type used for bootstrap lifecycle events. */
const BOOTSTRAP_EVENT_TYPE = 'accedo_bootstrap';
/** sessionStorage key holding the per-tab correlation id. */
const SESSION_ID_KEY = 'accedo.rum.sid';
/** App version reported to RUM. Static — the build is content-hash-versioned. */
const APP_VERSION = '1.0.0';

/** Token-bucket rate-limit: at most this many error events per window. */
const RATE_LIMIT_MAX = 10;
/** Rolling window (ms) for the rate-limit bucket. */
const RATE_LIMIT_WINDOW_MS = 60_000;

/**
 * Minimal surface of the AwsRum client we depend on. Keeping it narrow makes
 * the guarding obvious and avoids coupling to the full SDK type.
 */
interface RumClient {
    recordError(error: unknown): void;
    recordEvent(type: string, data: object): void;
}

let client: RumClient | null = null;
let lastRequestId: string | undefined;
/** Timestamps (ms) of recent error events, used for the rolling rate-limit. */
let errorTimestamps: number[] = [];

/**
 * Lazily returns a stable per-tab session id, persisted in sessionStorage so it
 * correlates every event from the same tab. Falls back to a fresh id (without
 * persisting) if sessionStorage is unavailable — telemetry still functions.
 */
export function getSessionId(): string {
    try {
        const existing = sessionStorage.getItem(SESSION_ID_KEY);
        if (existing) {
            return existing;
        }
        const id = crypto.randomUUID();
        sessionStorage.setItem(SESSION_ID_KEY, id);
        return id;
    } catch {
        return crypto.randomUUID();
    }
}

/** Records the most recent `x-amzn-RequestId` for cross-correlation. */
export function setLastRequestId(requestId: string): void {
    lastRequestId = requestId;
}

/** Returns the most recently captured `x-amzn-RequestId`, if any. */
export function getLastRequestId(): string | undefined {
    return lastRequestId;
}

/**
 * Installs a guarded wrapper around `window.fetch` that records the
 * `x-amzn-RequestId` response header for correlation. The wrapper is fully
 * transparent: it always returns the original response (or rejection) and never
 * alters fetch behaviour. Any error reading the header is swallowed.
 */
function installFetchWrapper(): void {
    try {
        const original = window.fetch.bind(window);
        window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
            const response = await original(...args);
            try {
                const requestId = response.headers.get('x-amzn-RequestId');
                if (requestId) {
                    setLastRequestId(requestId);
                }
            } catch {
                // Best-effort only — never let header capture affect the response.
            }
            return response;
        };
    } catch {
        // If we cannot wrap fetch, correlation is simply unavailable.
    }
}

/**
 * Initialises the RUM client from the resolved config. A `null` config (missing
 * or malformed `rum-config.json`) leaves RUM uninitialised and the app boots
 * normally. The constructor is wrapped in try/catch: if it throws, RUM stays
 * uninitialised and every record* call is a no-op. This function never throws.
 *
 * @param config - The resolved RUM config, or `null` to disable telemetry.
 */
export function initRum(config: RumConfig | null): void {
    if (!config) {
        return;
    }
    try {
        client = new AwsRum(config.appMonitorId, APP_VERSION, config.region, {
            identityPoolId: config.identityPoolId,
            // Pin the data-plane endpoint to the configured region (#3193). The
            // positional `region` arg alone is NOT sufficient: `aws-rum-web`'s
            // `defaultConfig` injects the SDK's us-west-2 default endpoint into
            // the merged config, so the base Orchestration constructor always
            // sees a truthy `partialConfig.endpoint` and its region-rewrite
            // branch never runs — telemetry POSTs to us-west-2 (403). Passing an
            // explicit endpoint built from `config.region` overrides that
            // default and is version-independent.
            endpoint: `https://dataplane.rum.${config.region}.amazonaws.com`,
            sessionSampleRate: 1.0,
            // Backend sink applies an additional 0.1 sample (#3018).
            telemetries: ['errors'],
            allowCookies: true,
        }) as unknown as RumClient;
        installFetchWrapper();
    } catch {
        // RUM is best-effort. A failed init must not break bootstrap.
        client = null;
    }
}

/** Builds the redacted, correlated metadata object attached to every event. */
function buildMetadata(metadata: Record<string, unknown> | undefined): Record<string, unknown> {
    const redacted = (metadata ? redactForRum(metadata) : {}) as Record<string, unknown>;
    return {
        ...redacted,
        sessionId: getSessionId(),
        ...(lastRequestId ? { requestId: lastRequestId } : {}),
    };
}

/**
 * Returns true if an error event is allowed under the rolling token bucket,
 * recording its timestamp. Bootstrap events bypass this entirely.
 */
function allowErrorEvent(now: number): boolean {
    errorTimestamps = errorTimestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
    if (errorTimestamps.length >= RATE_LIMIT_MAX) {
        return false;
    }
    errorTimestamps.push(now);
    return true;
}

/** Minimal error-like shape RUM's `recordError` accepts. */
interface ErrorLike {
    name: string;
    message: string;
    stack?: string;
}

/**
 * Returns true for an `Error` or any error-like object carrying a string
 * `name` and `message` (RUM ships these verbatim via the native JS-error event).
 */
function isErrorLike(value: unknown): value is ErrorLike {
    if (value instanceof Error) {
        return true;
    }
    if (value === null || typeof value !== 'object') {
        return false;
    }
    const v = value as Record<string, unknown>;
    return typeof v.name === 'string' && typeof v.message === 'string';
}

/**
 * Builds the error-like object passed to `client.recordError`, value-scrubbing
 * the free-text `message`/`stack` so PII/tokens embedded in them never reach RUM
 * (the key-based `redactForRum` cannot reach inside a free-text string). A
 * non-error value is stringified and scrubbed into a minimal error-like shape.
 */
function scrubErrorForRum(error: unknown): ErrorLike {
    if (isErrorLike(error)) {
        return {
            name: error.name,
            message: scrubValuePatterns(error.message ?? ''),
            stack: error.stack ? scrubValuePatterns(error.stack) : undefined,
        };
    }
    return { name: 'Error', message: scrubValuePatterns(String(error)) };
}

/**
 * Records an application error to RUM with redacted, correlated metadata.
 *
 * No-ops when RUM is uninitialised. Subject to the rolling rate limit
 * (bootstrap events are exempt — see {@link recordBootstrapEvent}). Guarded:
 * never throws.
 *
 * @param error - The error to record.
 * @param metadata - Optional structured context. PII is redacted and any
 *   request/response body is stripped before sending.
 */
export function recordError(error: unknown, metadata?: Record<string, unknown>): void {
    if (!client) {
        return;
    }
    try {
        if (!allowErrorEvent(Date.now())) {
            return;
        }
        // Value-scrub the native error before send — the metadata path below is
        // already key-redacted, but the raw error.message/stack is not.
        client.recordError(scrubErrorForRum(error));
        client.recordEvent(ERROR_EVENT_TYPE, buildMetadata(metadata));
    } catch {
        // Best-effort — a telemetry failure must never surface to the user.
    }
}

/**
 * Records a bootstrap lifecycle event (e.g. a retry or a bootstrap failure).
 *
 * These are rare and high-value, so they are EXEMPT from the error rate limit.
 * No-ops when RUM is uninitialised. Guarded: never throws.
 *
 * @param event - A short event name (e.g. `bootstrap_failed`, `bootstrap_retry`).
 * @param metadata - Optional structured context. PII is redacted and any
 *   request/response body is stripped before sending.
 */
export function recordBootstrapEvent(event: string, metadata?: Record<string, unknown>): void {
    if (!client) {
        return;
    }
    try {
        client.recordEvent(BOOTSTRAP_EVENT_TYPE, { event, ...buildMetadata(metadata) });
    } catch {
        // Best-effort — never break bootstrap.
    }
}

/**
 * Test-only reset of module state. Not exported from any barrel; used by unit
 * tests to isolate the singleton client and rate-limit bucket between cases.
 *
 * @internal
 */
export function __resetRumForTests(): void {
    client = null;
    lastRequestId = undefined;
    errorTimestamps = [];
}
